import React from 'react'

export type ModalCostBreakdownTone = 'neutral' | 'success' | 'info' | 'warning' | 'danger'

export type ModalCostBreakdownRow = {
  key: string
  label: React.ReactNode
  amountCredits?: number | null
  displayValue?: React.ReactNode
  explanation?: React.ReactNode
  tone?: ModalCostBreakdownTone
  isIncluded?: boolean
}

interface ModalCostBreakdownProps {
  rows: ModalCostBreakdownRow[]
  totalCredits?: number | null
  availableCredits?: number | null
  emptyMessage?: React.ReactNode
  formatCredits?: (value: number) => string
  footerNote?: React.ReactNode
}

const defaultFormatCredits = (value: number) =>
  new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)))

const formatAmount = (
  row: ModalCostBreakdownRow,
  formatCredits: (value: number) => string,
) => {
  if (row.displayValue != null) return row.displayValue
  if (row.isIncluded) return 'Included'
  if (typeof row.amountCredits === 'number' && Number.isFinite(row.amountCredits)) {
    return `${formatCredits(row.amountCredits)} credits`
  }
  return 'Not calculated'
}

const ModalCostBreakdown: React.FC<ModalCostBreakdownProps> = ({
  rows,
  totalCredits,
  availableCredits,
  emptyMessage = 'Select options to calculate cost.',
  formatCredits = defaultFormatCredits,
  footerNote,
}) => {
  const hasRows = rows.length > 0
  const hasTotal = typeof totalCredits === 'number' && Number.isFinite(totalCredits)
  const hasAvailable = typeof availableCredits === 'number' && Number.isFinite(availableCredits)
  const remainingCredits = hasTotal && hasAvailable
    ? Math.max(availableCredits! - totalCredits!, 0)
    : null

  return (
    <div className="modal-cost-breakdown">
      <div className="modal-cost-breakdown__rows">
        {hasRows ? rows.map((row) => (
          <div
            key={row.key}
            className={`modal-cost-breakdown__row modal-cost-breakdown__row--${row.tone || 'neutral'}`}
          >
            <div className="modal-cost-breakdown__label">
              <span>{row.label}</span>
              {row.explanation ? (
                <span className="modal-cost-breakdown__explanation">{row.explanation}</span>
              ) : null}
            </div>
            <strong className="modal-cost-breakdown__value">
              {formatAmount(row, formatCredits)}
            </strong>
          </div>
        )) : (
          <div className="modal-cost-breakdown__empty">{emptyMessage}</div>
        )}
      </div>
      <div className="modal-cost-breakdown__totals" aria-live="polite">
        <div>
          <span>Required credits</span>
          <strong>{hasTotal ? formatCredits(totalCredits!) : 'Not calculated'}</strong>
        </div>
        <div>
          <span>Available credits</span>
          <strong>{hasAvailable ? formatCredits(availableCredits!) : 'Loading...'}</strong>
        </div>
        <div>
          <span>Remaining after run</span>
          <strong>{remainingCredits == null ? 'Not calculated' : formatCredits(remainingCredits)}</strong>
        </div>
      </div>
      {footerNote ? <div className="modal-cost-breakdown__note">{footerNote}</div> : null}
    </div>
  )
}

export default ModalCostBreakdown
